import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead } from "@/pages/admin/AdminLayout";
import { PlanCard } from "@/pages/Pricing";
import { api, formatApiError, longDate } from "@/lib/api";

const STATUS_TEXT = {
  active: "Active", trialing: "Trial", comp: "Complimentary (assigned by platform)", past_due: "Payment overdue",
  canceled: "Cancelled", incomplete: "Payment incomplete", incomplete_expired: "Expired", unpaid: "Unpaid", none: "No plan",
};

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/billing").then((r) => { setB(r.data); window.dispatchEvent(new Event("clinic-updated")); });

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { load(); return; }
    let tries = 0;
    const poll = async () => {
      const { data } = await api.get(`/admin/billing/session/${sid}`).catch(() => ({ data: {} }));
      if (data.status === "complete" || tries > 10) {
        if (data.status === "complete") toast.success("Subscription active — your clinic plan is live");
        setParams({});
        load();
        return;
      }
      tries += 1;
      setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/admin/billing/subscribe", { plan_id: p.id, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(`Switched to ${p.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const setCancel = async (cancel) => {
    try { await api.post("/admin/billing/cancel", { cancel }); toast.success(cancel ? "Plan will end at the period end" : "Plan renewed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!b) return <Loader2 className="animate-spin text-primary" data-testid="billing-loading" />;
  const current = b.plans.find((p) => p.id === b.plan_id);
  const live = ["active", "trialing", "comp"].includes(b.subscription_status);

  return (
    <div className="fade-up">
      <PageHead eyebrow="Subscription" title="Plan & billing" />
      <div className="rounded-2xl bg-card border border-border p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="billing-current">
        <div>
          <p className="text-sm text-muted-foreground">Current plan</p>
          <p className="font-display font-bold text-lg" data-testid="billing-current-plan">{live && current ? current.name : "No active plan"}</p>
          <p className="text-sm" data-testid="billing-status">{STATUS_TEXT[b.subscription_status || "none"] || b.subscription_status}
            {b.current_period_end && b.subscription_status !== "comp" && ` · ${b.cancel_at_period_end ? "ends" : "renews"} ${longDate(b.current_period_end.slice(0, 10))}`}</p>
          <p className="text-xs text-muted-foreground mt-1">Usage: {b.usage.practitioners} practitioners · {b.usage.services} treatments · {b.listed ? "Listed on marketplace" : "Not listed on marketplace"}</p>
        </div>
        {b.has_subscription && live && b.subscription_status !== "comp" && (
          b.cancel_at_period_end
            ? <Button variant="outline" className="rounded-full" onClick={() => setCancel(false)} data-testid="billing-resume-button">Keep my plan</Button>
            : <Button variant="outline" className="rounded-full" onClick={() => setCancel(true)} data-testid="billing-cancel-button">Cancel plan</Button>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {b.plans.map((p) => {
          const isCurrent = live && p.id === b.plan_id;
          return (
            <PlanCard key={p.id} p={p} highlight={isCurrent} action={
              <Button className="w-full rounded-full" disabled={isCurrent || busy || b.subscription_status === "comp"} onClick={() => choose(p)} data-testid={`billing-choose-${p.id}`}>
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Current plan" : b.has_subscription && live ? `Switch to ${p.name}` : `Subscribe to ${p.name}`}
              </Button>
            } />
          );
        })}
      </div>
      {b.subscription_status === "comp" && <p className="text-sm text-muted-foreground mt-4">Your plan was assigned by the platform team. Contact them to change it.</p>}
    </div>
  );
}
